import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Archive, Download, EnvelopeSimple, Trash, Warning, CheckCircle, ShieldWarning, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modale de suppression sécurisée en 3 étapes :
 *  1. archive     — Télécharger archive ZIP (<24h obligatoire)
 *  2. code        — Recevoir code 6 chiffres par email
 *  3. confirm     — Saisir code + nom exact du chantier
 */
export default function DeleteChantierSecureDialog({ open, onOpenChange, chantierId, chantierNom, onDeleted }) {
  const [step, setStep] = useState('archive');
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [code, setCode] = useState('');
  const [nomSaisi, setNomSaisi] = useState('');
  const [error, setError] = useState('');

  // Archive state
  const [jobId, setJobId] = useState(null);
  const [archiveStatus, setArchiveStatus] = useState('idle'); // idle | processing | completed | failed
  const [archiveProgress, setArchiveProgress] = useState(0);
  const [archiveStep, setArchiveStep] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // reset
      setTimeout(() => {
        setStep('archive'); setEligibility(null); setCode(''); setNomSaisi(''); setError('');
        setJobId(null); setArchiveStatus('idle'); setArchiveProgress(0); setArchiveStep('');
        if (pollRef.current) clearInterval(pollRef.current);
      }, 250);
      return;
    }
    checkEligibility();
  }, [open, chantierId]);

  const checkEligibility = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/deletion-eligibility`,
        { withCredentials: true },
      );
      setEligibility(data);
      setStep(data.archive_ready ? 'code' : 'archive');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Accès refusé');
      onOpenChange(false);
    }
  };

  const launchArchive = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/export-archive`,
        {}, { withCredentials: true },
      );
      setJobId(data.job_id);
      setArchiveStatus('processing');
      // Poll toutes les 2s
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await axios.get(
            `${API_URL}/api/chantiers/${chantierId}/export-archive/${data.job_id}`,
            { withCredentials: true },
          );
          setArchiveProgress(st.progress || 0);
          setArchiveStep(st.current_step || '');
          if (st.status === 'completed') {
            setArchiveStatus('completed');
            clearInterval(pollRef.current);
          } else if (st.status === 'failed') {
            setArchiveStatus('failed');
            setError(st.error_message || 'Échec de la génération');
            clearInterval(pollRef.current);
          }
        } catch {/* ignore transient errors */}
      }, 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lancement archive');
    } finally { setLoading(false); }
  };

  const downloadArchive = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/export-archive/${jobId}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1] || `archive_${chantierId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Archive téléchargée');
      // Refresh eligibility → passe à l'étape "code"
      await checkEligibility();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const requestCode = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/deletion-request`,
        {}, { withCredentials: true },
      );
      setExpiresAt(new Date(data.expires_at));
      setStep('confirm');
      toast.success(data.message);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur envoi code');
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/deletion-confirm`,
        { code, chantier_nom: nomSaisi },
        { withCredentials: true },
      );
      toast.success(data.message);
      onDeleted?.(data.report);
      onOpenChange(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur confirmation');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none" data-testid="delete-secure-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#8B3A3A]">
            <ShieldWarning size={22} weight="fill" />
            Supprimer « {chantierNom} »
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B6B]">
            Action irréversible — comptes rendus, plans, photos, factures, vocaux, messages : tout sera supprimé.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-2 mb-4 text-xs">
          {[
            { k: 'archive', label: 'Archive' },
            { k: 'code', label: 'Code email' },
            { k: 'confirm', label: 'Confirmation' },
          ].map((s, i, arr) => {
            const order = arr.findIndex(x => x.k === step);
            const current = order;
            const done = i < current;
            const active = i === current;
            return (
              <div key={s.k} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? 'bg-[#8B3A3A] text-white' : done ? 'bg-[#7B9E89] text-white' : 'bg-[#E0E0DB] text-[#9A9A9A]'}`}>
                  {done ? '✓' : i + 1}
                </div>
                <span className={`${active ? 'text-[#2C2C2C] font-medium' : 'text-[#9A9A9A]'}`}>{s.label}</span>
                {i < 2 && <div className={`flex-1 h-[1px] ${done ? 'bg-[#7B9E89]' : 'bg-[#E0E0DB]'}`} />}
              </div>
            );
          })}
        </div>

        {/* ÉTAPE 1 : ARCHIVE */}
        {step === 'archive' && (
          <div className="space-y-4" data-testid="delete-step-archive">
            <div className="flex items-start gap-3 bg-[#FDF6EC] border-l-4 border-[#C4773B] p-3">
              <Info size={20} className="text-[#C4773B] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#6B4A28]">
                Vous devez <strong>télécharger une archive ZIP complète</strong> du dossier avant de pouvoir
                supprimer le chantier. L'archive doit dater de moins de <strong>24h</strong>.
              </p>
            </div>

            {archiveStatus === 'idle' && (
              <Button onClick={launchArchive} disabled={loading} className="rounded-none bg-[#1E3A5F] hover:bg-[#172C47] text-white w-full" data-testid="delete-launch-archive">
                <Archive size={16} className="mr-2" /> Générer l'archive ZIP
              </Button>
            )}

            {archiveStatus === 'processing' && (
              <div className="space-y-2" data-testid="delete-archive-progress">
                <p className="text-xs text-[#6B6B6B]">{archiveStep || 'Préparation...'}</p>
                <div className="h-2 bg-[#E0E0DB] w-full">
                  <div className="h-full bg-[#1E3A5F] transition-all" style={{ width: `${archiveProgress}%` }} />
                </div>
                <p className="text-[10px] text-[#9A9A9A] text-right">{archiveProgress}%</p>
              </div>
            )}

            {archiveStatus === 'completed' && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 bg-[#E8F3EA] border-l-4 border-[#7B9E89] p-3">
                  <CheckCircle size={20} className="text-[#7B9E89]" weight="fill" />
                  <p className="text-xs text-[#2D5541]">Archive prête. Téléchargez-la pour débloquer l'étape suivante.</p>
                </div>
                <Button onClick={downloadArchive} className="rounded-none bg-[#7B9E89] hover:bg-[#6C8D7A] text-white w-full" data-testid="delete-download-archive">
                  <Download size={16} className="mr-2" /> Télécharger l'archive
                </Button>
              </div>
            )}

            {archiveStatus === 'failed' && error && (
              <div className="bg-[#FEF2F2] border-l-4 border-[#8B3A3A] p-3">
                <p className="text-xs text-[#8B3A3A]">{error}</p>
              </div>
            )}
          </div>
        )}

        {/* ÉTAPE 2 : CODE */}
        {step === 'code' && eligibility && (
          <div className="space-y-4" data-testid="delete-step-code">
            <div className="flex items-start gap-2 bg-[#E8F3EA] border-l-4 border-[#7B9E89] p-3">
              <CheckCircle size={20} className="text-[#7B9E89]" weight="fill" />
              <div>
                <p className="text-xs font-medium text-[#2D5541]">Archive téléchargée ✓</p>
                <p className="text-[10px] text-[#4B7A5A] mt-0.5">
                  {eligibility.archive_downloaded_at ? new Date(eligibility.archive_downloaded_at).toLocaleString('fr-FR') : ''}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 border border-[#E0E0DB] p-3">
              <EnvelopeSimple size={20} className="text-[#1E3A5F] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2C2C2C]">Code de confirmation</p>
                <p className="text-xs text-[#6B6B6B] mt-1">
                  Un code à 6 chiffres sera envoyé à <strong>{eligibility.user_email}</strong>. Valable 15 minutes, max 3 essais.
                </p>
                <Button onClick={requestCode} disabled={loading} className="mt-3 rounded-none bg-[#8B3A3A] hover:bg-[#6E2D2D] text-white" data-testid="delete-request-code">
                  {loading ? 'Envoi...' : 'Recevoir le code par email'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE 3 : CONFIRM */}
        {step === 'confirm' && (
          <div className="space-y-4" data-testid="delete-step-confirm">
            <div className="flex items-start gap-2 bg-[#FEF2F2] border-l-4 border-[#8B3A3A] p-3">
              <Warning size={20} className="text-[#8B3A3A]" weight="fill" />
              <p className="text-xs text-[#6E2D2D]">
                <strong>Dernière étape.</strong> Saisissez le code reçu et le nom exact du chantier pour supprimer définitivement.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide">Code reçu par email</Label>
              <Input
                type="text" inputMode="numeric" maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-2xl font-mono tracking-[0.5em] text-center mt-1 rounded-none h-14"
                autoComplete="off"
                data-testid="delete-code-input"
              />
              {expiresAt && (
                <p className="text-[10px] text-[#9A9A9A] mt-1 text-right">
                  Expire à {expiresAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide">
                Saisissez le nom exact
              </Label>
              <p className="text-[11px] text-[#6B6B6B] my-1">Attendu : <code className="bg-[#F5F5F3] px-1 font-mono">{chantierNom}</code></p>
              <Input
                value={nomSaisi}
                onChange={(e) => setNomSaisi(e.target.value)}
                placeholder={chantierNom}
                className="rounded-none mt-1"
                data-testid="delete-nom-input"
              />
            </div>
          </div>
        )}

        {error && step !== 'archive' && (
          <div className="bg-[#FEF2F2] border-l-4 border-[#8B3A3A] p-2 text-xs text-[#8B3A3A]" data-testid="delete-error">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-[#E0E0DB]">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="delete-cancel">
            Annuler
          </Button>
          {step === 'confirm' && (
            <Button
              onClick={confirm}
              disabled={loading || code.length !== 6 || !nomSaisi.trim()}
              className="rounded-none bg-[#8B3A3A] hover:bg-[#6E2D2D] text-white gap-2"
              data-testid="delete-confirm-btn"
            >
              <Trash size={14} /> {loading ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
